<template>
	<div tabindex="0" class="hide-outline">
		<main v-if="loaded" class="main-view no-top-panel">
		<SyncToast
		:state="syncState"
		:dirty="dirty"
		:has-local-draft="hasLocalDraft"
		:is-online="isOnline"
		/>
			<div class="is-flex is-justify-content-space-between">
				<div class="title">{{ round.name }}</div>
				<div class="buttons has-addons is-flex is-justify-content-flex-end">
					<button
						class="button is-success is-half"
						@click="finishRound"
						:disabled="!canFinishRound"
						:class="manualSaving ? 'is-loading' : ''"
						:title="!isOnline ? $t('round_view.finish_requires_online_tooltip') : null"
					>
						<span class="icon is-white"><i class="fas fa-clipboard-check"/></span>
						<span>{{ $t('round_view.finish_round') }}</span>
					</button>
					<button
						class="button is-half"
						@click="pauseRound"
						:class="manualSaving ? 'is-loading' : ''"
					>
						<span class="icon"><i class="fas fa-pause"/></span>
						<span>{{ $t('round_view.pause_round') }}</span>
					</button>
				</div>
			</div>

      <TabComponent :tabs="tabs" v-model="selectedTab" :fullwidth="isMobile" />
      <MapComponent
        :map="round.map"
        :infos="activeRound.infos"
        v-if="selectedTab === 0"
        @addFlaw="addFlaw"
        @saveState="saveState"
        @update="loadState"
        ref="mapview"
      />
      <ProtectionsList
        :infos="activeRound.infos"
        v-if="selectedTab === 1"
        @addFlaw="addFlaw"
        @saveState="saveState"
        @update="changeInfo"
        ref="protectionlist"
      />
     <Checklist
		:checklists="activeRound.checklists"
		v-if="selectedTab === 2"
		@saveState="saveState"
		@syncNow="forceSyncNow"
	  />
      <SummaryComponent
        v-if="selectedTab === 3"
        :infos="activeRound.infos"
        :checklists="activeRound.checklists"
        :success="success"
		:is-online="isOnline"
        :saving="manualSaving"
        @allOk="setAllOk"
        @pauseRound="pauseRound"
        @finishRound="finishRound"
      />
      <AddFlawModal
		v-if="showFlawModal"
		:ctx="flawModalCtx"
		:round="activeRound"
		@cancel="toggleFlawModal"
		@flaw-added="flawAdded"
		@flaw-fixed="setFixed"
		@continueWithoutAdding="continueWithoutAddingFlaw"
		@service-info-queued="onServiceInfoQueued"
		/>
    </main>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex';
import TabComponent from '@/components/TabComponent';
import MapComponent from './components/Map';
import ProtectionsList from './components/ProtectionsList';
import Checklist from './components/Checklist';
import SummaryComponent from './components/Summary';
import AddFlawModal from './modals/AddFlawModal.vue';
import debounce from 'lodash/debounce';
import { putOfflineFile, getOfflineFile, removeOfflineFile } from '@/services/offlineFiles';
import SyncToast from './components/SyncToast.vue';
import { listPendingMapItemExtras, removePendingMapItemExtra } from '@/services/offlineMapItemExtra';
import MapItemService from '@/services/mapItem';
import FlawService from '@/services/flaw';
import { saveFlawTaxonomy, loadFlawTaxonomy } from '@/services/offlineFlawTaxonomy';
import { removeOfflineFilesForRound } from '@/services/offlineFiles';
import { clearPendingMapItemExtrasForRound } from '@/services/offlineMapItemExtra';
import { getRoundDraft, putRoundDraft, removeRoundDraft, hasRoundDraft  } from '@/services/offlineRoundDraft';


export default {
	components: {
		TabComponent,
		MapComponent,
		ProtectionsList,
		Checklist,
		SummaryComponent,
		AddFlawModal,
		SyncToast,
	},
	data: () => ({
		selectedTab: 1,
		roundData: null,
		tabs: [],
		hasDraftInIdb: false,
		loaded: false,
		success: null,
		manualSaving: false, 
		autosaving: false,
		dirty: false, 
		isDone: false,
		showFlawModal: false,
		autosaveReady: false,
		debouncedAutosave: null,
		saveMode: 'server',
		syncInFlight: false,
		isLeavingView: false,
		isOnline: navigator.onLine,
		flawModalCtx: {
			origin: null,      
			infoIdx: 0,        
			rowIdx: null,      
			looseIdx: null     
		},
		pendingMapItemExtraCount: 0,
	}),
	computed: {
		...mapGetters('roundStore', ['round']),
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('roundHistoryStore', ['activeRound']),
		...mapGetters('roundHistoryInfoStore', ['roundHistoryInfos']),
		...mapState('roundHistoryInfoStore', {
			infoParams: 'searchParams'
		}),
		...mapGetters('roundHistoryQuestionStore', ['roundHistoryQuestionLists']),
		...mapState('roundHistoryQuestionStore', {
			questionParams: 'searchParams'
		}),
		...mapState('fileStore', ['file']),
		...mapGetters('user', ['getUser']),
		mobile() {
			return this.isMobile ? { display: 'block' } : '';
		},

		canFinishRound() {
			return this.isOnline && !this.disableDone;
		},

		hasPendingUnsynced() {
			return (
				this.dirty ||
				this.hasLocalDraft ||
				this.pendingUploadCount > 0 ||
				this.pendingMapItemExtraCount > 0 ||
				this.saveMode === 'local'
			);
		},

  totProtections() {
    const infos = this.activeRound?.infos || this.round?.infos || [];
    return infos
      .flatMap(info => info.roundHistoryItems || [])
      .filter(item =>
        item &&
        item.id &&
        item.positionNumber != null &&
        item.inventoryType
      );
  },
  checkedItems() {
    return this.totProtections.filter(
      item => item.controlled || item.continueWithoutAdding
    );
  },

		questionData() {
			let data = [0, 0, 0];
			this.questions.forEach((question) => {
				if (question.answer === question.positive || question.answer === '') {
					data[0]++;
				} else if (question.answer === question.negative) {
					data[1]++;
				} else {
					data[2]++;
				}
			});

			return data;
		},

		questions() {
			const list = [];
			(this.activeRound?.checklists || []).forEach((checklist) => {
				(checklist.roundHistoryQuestions || []).forEach((question) => {
				list.push(question);
				});
			});
			return list;
		},

		disableDone() {
			const itemsLeft = this.totProtections.length - this.checkedItems.length;

			const questionsLeft =
			this.questionData[0] + this.questionData[1] - this.questions.length;

			return itemsLeft !== 0 || questionsLeft !== 0;
		},
		pendingUploadCount() {
			const infos = (this.roundData?.infos || this.activeRound?.infos || []);
			let count = 0;

			infos.forEach(info => {
				(info.roundHistoryItems || []).forEach(item => {
				(item.flaws || []).forEach(f => {
					if (f?.fileLocalKey || f?.filePendingUpload) count++;
				});
				});
				(info.flaws || []).forEach(f => {
				if (f?.fileLocalKey || f?.filePendingUpload) count++;
				});
			});

			return count;
		},


		hasLocalDraft() {
			const unsynced = this.dirty || this.saveMode === 'local' || this.pendingUploadCount > 0;
			return unsynced && this.hasDraftInIdb;
		},


		syncState() {
			if (!this.isOnline) {
				return {
				key: 'offline',
				tone: 'warning',
				title: this.$t?.('sync.offline_title') ?? 'Offline',
				message:
					this.dirty || this.hasLocalDraft || this.pendingUploadCount > 0
					? (this.$t?.('sync.offline_dirty') ??
						'Changes are saved on this device and will sync when you’re back online.')
					: (this.$t?.('sync.offline_clean') ??
						'You’re offline. No unsynced changes right now.'),
				action: null
				};
			}

			if (this.manualSaving || this.autosaving || this.syncInFlight) {
				return {
				key: 'syncing',
				tone: 'info',
				title: this.$t?.('sync.saving_title') ?? 'Saving…',
				message: this.$t?.('sync.saving_msg') ?? 'Saving changes to the server. You can keep working.',
				action: null
				};
			}

			if (
			this.dirty ||
			this.hasLocalDraft ||
			this.pendingUploadCount > 0 ||
			this.pendingMapItemExtraCount > 0 ||
			this.saveMode === 'local'
			) {
				const parts = [];
				if (this.dirty || this.hasLocalDraft || this.saveMode === 'local') parts.push('form changes');
				if (this.pendingUploadCount > 0) parts.push(`${this.pendingUploadCount} photo${this.pendingUploadCount > 1 ? 's' : ''}`);
				if (this.pendingMapItemExtraCount > 0) parts.push(`${this.pendingMapItemExtraCount} service info`);


				return {
				key: 'pending',
				tone: 'warning',
				title: this.$t?.('sync.pending_title') ?? 'Not synced yet',
				message:
					(this.$t?.('sync.pending_msg') ?? 'Some changes are only saved locally and will sync soon.') +
					(parts.length ? ` (Pending: ${parts.join(', ')})` : ''),
				action: {
					label: this.$t?.('sync.sync_now') ?? 'Sync now',
					onClick: () => this.forceSyncNow()
				}
				};
			}

			return {
				key: 'synced',
				tone: 'success',
				title: this.$t?.('sync.synced_title') ?? 'All changes saved',
				message: this.$t?.('sync.synced_msg') ?? 'Everything is safely saved to the server.',
				action: null
			};
		}

	},
	watch: {
		selectedTab(to) {
			if (!this.roundData) return;
			this.roundData.currentTab = to;

			this.saveStateSilent(this.roundData);

			var viewport = document.querySelector('meta[name="viewport"]');
			if (viewport) {
				viewport.content = 'initial-scale=1';
				viewport.content = `width=${window.innerWidth}`;
			}
		},
		'syncState.key'(next, prev) {
			if (next === prev) return;

			if (next === 'offline') this.$toast?.warning?.('You are offline. Changes will sync later.');
			if (next === 'syncing' && prev === 'offline') this.$toast?.info?.('Back online — syncing changes…');
			if (next === 'synced' && (prev === 'pending' || prev === 'syncing')) this.$toast?.success?.('All changes saved.');
		}
	},
	methods: {
		...mapMutations('appStore', ['SET_COLLAPSED']),
		...mapMutations('fileStore', ['AddRoundImage', 'ResetRoundImages']),
		...mapActions('roundStore', ['setRound']),
		...mapActions('roundHistoryQuestionStore', [
			'updateRoundHistoryQuestion',
			'getRoundHistoryQuestionLists'
		]),

		
		...mapActions('roundHistoryItemStore', ['updateRoundHistoryItem']),
		...mapActions('roundHistoryInfoStore', ['getRoundHistoryInfos']),
		...mapActions('flawStore', ['createFlaw', 'updateFlaw']),
		...mapActions('roundHistoryStore', ['updateRoundHistory']),
		...mapActions('fileStore', ['saveFile', 'getFile']),
		...mapMutations('roundHistoryStore', ['SET_ACTIVEROUND']),

		async cleanupOfflineForRound(roundId) {
			if (!roundId) return;

			await removeRoundDraft(roundId).catch(() => {});
			this.hasDraftInIdb = false; // immediate UI update

			await clearPendingMapItemExtrasForRound(roundId).catch(() => {});
			this.pendingMapItemExtraCount = 0;

			await removeOfflineFilesForRound(roundId).catch(() => {});
		},
		async refreshHasDraft() {
			const roundId = this.activeRound?.id;
			this.hasDraftInIdb = roundId ? await hasRoundDraft(roundId) : false;
		},
		

		estimateBytes(str) {
			return (str?.length || 0) * 2;
		},

		async preloadFlawTaxonomy() {
			const lang = this.$i18n?.locale || 'en';

			const cached = loadFlawTaxonomy(lang);
			if (cached?.categories?.length) return;

			if (!navigator.onLine) return;

			try {
				const resp = await FlawService.GetFlawCategoriesWithFlawTypes(lang);
				const raw = resp?.data?.data ?? resp?.data ?? resp;
				const cats = Array.isArray(raw) ? raw : (raw?.categories || []);
				saveFlawTaxonomy(lang, cats || []);
			} catch (e) {
			}
		},

		async wipeRoundLocalData(roundId) {
			await this.cleanupOfflineForRound(roundId);
		},

		async onServiceInfoQueued() {
			this.saveMode = 'local';
			this.dirty = true; 
			await this.refreshPendingMapItemExtraCount();
		},

		async refreshPendingMapItemExtraCount() {
			const roundId = this.activeRound?.id;
			if (!roundId) {
				this.pendingMapItemExtraCount = 0;
				return;
			}
			try {
				const rows = await listPendingMapItemExtras(roundId);
				this.pendingMapItemExtraCount = rows.length;
			} catch (e) {
				this.pendingMapItemExtraCount = 0;
			}
		},


		toggleFlawModal() {
			this.showFlawModal = !this.showFlawModal;
		},

		toIntOrUndef(v) {
			if (v === null || v === undefined) return undefined;
			if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
			if (typeof v === 'string') {
				const s = v.trim();
				if (s === '') return undefined;
				const n = Number(s);
				if (!Number.isFinite(n)) return undefined;
				return Math.trunc(n);
			}
			return undefined;
		},

		normalizeFlawForCreate(flaw) {
			const dto = { ...flaw };

			delete dto.file;
			delete dto.fileLocalKey;
			delete dto.filePendingUpload;

			if (!dto.roundHistoryItemId) delete dto.roundHistoryItemId;
			if (!dto.itemId) delete dto.itemId;

			const x = this.toIntOrUndef(dto.flawPositionX);
			const y = this.toIntOrUndef(dto.flawPositionY);

			if (x === undefined) delete dto.flawPositionX;
			else dto.flawPositionX = x;

			if (y === undefined) delete dto.flawPositionY;
			else dto.flawPositionY = y;

			if (dto.description === '') dto.description = null;

			return dto;
		},

		async ensureBlob(maybeFile) {
			if (maybeFile instanceof Blob) return maybeFile;

			if (maybeFile?.blob instanceof Blob) return maybeFile.blob;

			if (typeof maybeFile === 'string' && maybeFile.startsWith('data:')) {
				const res = await fetch(maybeFile);
				return await res.blob();
			}

			if (typeof maybeFile?.dataUrl === 'string' && maybeFile.dataUrl.startsWith('data:')) {
				const res = await fetch(maybeFile.dataUrl);
				return await res.blob();
			}

			if (maybeFile instanceof ArrayBuffer) {
				return new Blob([maybeFile]);
			}

			return null;
		},

		deepClone(obj) {
			return JSON.parse(JSON.stringify(obj));
		},

		findOldFlawById(oldContainer, flawId) {
			const list = oldContainer?.flaws || [];
			return list.find(f => f?.id === flawId) || null;
		},

		async autosave() {
			if (this.isLeavingView) return;
			await this.persistDraftLocally();
			
			if (!this.dirty) return;
			
			if (this.autosaving) return;
			
			if (!navigator.onLine) {
				this.saveMode = 'local';
				return;
			}
			
			this.autosaving = true;
			try {
				const snapshot = this.deepClone(this.activeRound);
			
				const ok = await this.bulkSave(snapshot, { showSpinner: false });
				this.saveMode = ok ? 'server' : 'local';
			
				if (ok) {
					await this.flushPendingMapItemExtras();
					await this.flushPendingUploads();

					await this.cleanupOfflineForRound(this.activeRound.id);

					this.dirty = false;
					this.saveMode = 'server';
				}
			} finally {
				this.autosaving = false;
			}
		},

		async persistDraftLocally() {
			if (this.isLeavingView) return;
			if (!this.activeRound?.id) return;

			const draft = { ts: Date.now(), round: this.activeRound };

			try {
				await putRoundDraft(this.activeRound.id, draft);
			} catch (e) {
				this.$toast?.error?.('Offline storage failed. Please reconnect and sync.');
				throw e;
			}

			await this.refreshHasDraft();
		},

		async clearLocalDraft() {
			if (!this.activeRound?.id) return;
			await removeRoundDraft(this.activeRound.id);
			await this.refreshHasDraft();      
		},
		setFixed(item) {
			this.loadState();
			this.selectedTab === 0
				? this.$refs.mapview.flawAdded(item)
				: this.$refs.protectionlist.flawAdded(item);
		},
		flawAdded(payload) {

		
			const roundData = this.deepClone(this.activeRound);
			const { origin, infoIdx, rowIdx, looseIdx } = this.flawModalCtx || {};
			const info = roundData?.infos?.[infoIdx ?? 0];
			if (!info) return;

			const newFlaw = { ...(payload || {}) };
			if (!newFlaw.id && !newFlaw.clientKey) {
				newFlaw.clientKey = `ck_${Date.now()}_${Math.random().toString(16).slice(2)}`;
			}

			if (!Array.isArray(info.flaws)) this.$set(info, 'flaws', []);
			if (!Array.isArray(info.roundHistoryItems)) this.$set(info, 'roundHistoryItems', []);
			info.roundHistoryItems = Array.isArray(info.roundHistoryItems) ? info.roundHistoryItems : [];



			if (origin === 'loose') {
				delete newFlaw.roundHistoryItemId;
				delete newFlaw.itemId;
				newFlaw.roundHistoryInfoId = info.id;

				if (Number.isInteger(looseIdx) && looseIdx >= 0 && looseIdx < info.flaws.length) {
				info.flaws.splice(looseIdx, 1, newFlaw);
				} else {

				console.log('ADD FLAW', {
					origin,
					infoIdx,
					selectedInfoId: this.activeRound?.currentInfoId,
					infoId: roundData?.infos?.[infoIdx]?.id,
					itemsBefore: roundData?.infos?.[infoIdx]?.roundHistoryItems?.length,
					looseBefore: roundData?.infos?.[infoIdx]?.flaws?.length,
					});
					info.flaws.push(newFlaw);
				}
			} else {
				const rowItemId = this.flawModalCtx?.rowItemId;
				const item = (info.roundHistoryItems || []).find(it => String(it.id) === String(rowItemId));
				if (!item) { console.warn('item not found', { rowItemId }); return; }

				if (!item) {
				console.warn('flawAdded: item not found for rowItemId', { rowItemId });
				this.showFlawModal = false;
				return;
				}

				if (!Array.isArray(item.flaws)) this.$set(item, 'flaws', []);

				newFlaw.roundHistoryItemId = item.id;
				newFlaw.roundHistoryInfoId = info.id;
				item.flaws.push(newFlaw);
				const now = this.setDate ? this.setDate() : new Date();
				item.controlled = now;
				item.userName = this.getUser?.email || item.userName;
			}

			this.showFlawModal = false;
			this.saveState(roundData);     

			this.setRound(roundData);
			this.$nextTick(() => {
				this.$refs?.mapview?.updateItems?.();
				this.$refs?.protectionlist?.updateItems?.();
				this.$refs?.mapview?.reopenGroupFromCtx?.(this.flawModalCtx);
			});

		
		},

		patchFlawIdByClientKey(clientKey, id) {
			const round = this.deepClone(this.activeRound);

			for (const info of (round.infos || [])) {
				for (const f of (info.flaws || [])) {
					if (f?.clientKey === clientKey) f.id = id;
					}
				for (const item of (info.roundHistoryItems || [])) {
					for (const f of (item.flaws || [])) {
						if (f?.clientKey === clientKey) f.id = id;
					}
				}
			}

			this.saveStateSilent(round);     
			this.roundData = round;        
		},

		addFlaw(index, origin, prefs) {
			const infoId = prefs?.infoId ?? this.activeRound?.currentInfoId ?? null;

			let infoIdx = 0;
			if (infoId) {
				const found = (this.activeRound?.infos || []).findIndex(i => i.id === infoId);
				infoIdx = found >= 0 ? found : 0;
			} else {
				infoIdx = (this.activeRound?.currentInfo ?? 0);
			}

			this.flawModalCtx = {
				origin,
				infoIdx,
				rowIdx: origin === 'row' ? index : null,
				rowItemId: prefs?.rowItemId || null,
				looseIdx: origin === 'loose' ? index : null,

				fromGroup: !!prefs?.fromGroup,
				groupPos: prefs?.groupPos || null,

				editingFlawId: prefs?.editingFlawId || null,
				editingClientKey: prefs?.editingClientKey || null,
				prefCategoryId: prefs?.prefCategoryId || null,
				prefCategoryKey: prefs?.prefCategoryKey || null,
			};

			this.showFlawModal = true;
		},

		continueWithoutAddingFlaw(item) {
			this.roundData = this.deepClone(this.activeRound);

			const info = this.roundData.infos.find((x) =>
				x.roundHistoryItems.some((y) => y.id === item.id)
			);

			if (!info) return;

			const it = info.roundHistoryItems.find((x) => x.id === item.id);
			if (!it) return;

			it.continueWithoutAdding = true;
			it.controlled = this.setDate();

			this.showFlawModal = false;

			this.saveState(this.roundData);

			this.$refs?.protectionlist?.$forceUpdate?.();
		},

		markDirty() {
			if (!this.loaded || !this.autosaveReady) return;

			this.dirty = true;

			if (this.syncInFlight || this.manualSaving || this.autosaving) return;

			this.debouncedAutosave?.();
		},
		saveState(payload) {
			this.SET_ACTIVEROUND(payload || this.roundData || this.activeRound);
			this.markDirty();
		},
		loadState() {
			this.roundData = this.deepClone(this.activeRound);
			this.saveStateSilent(this.roundData);
			this.setRound(this.roundData);
		},
		saveStateSilent(payload) {
			this.SET_ACTIVEROUND(payload || this.roundData || this.activeRound);
		},
		saveLoad() {
			this.saveStateSilent();
			this.$nextTick(() => {
				this.roundData = this.deepClone(this.activeRound);
				this.saveStateSilent(this.roundData);
				this.setRound(this.roundData);
			});
		},

		changeInfo() {
			this.roundData = this.deepClone(this.activeRound);
			this.saveState(this.roundData);
		},

		async asyncForEach(array, callback) {
			for (let index = 0; index < array.length; index++) {
				await callback(array[index], index, array);
			}
		},
		async bulkSave(roundSnapshot = null, { showSpinner = true } = {}) {
			if (this.syncInFlight) return false;
			this.syncInFlight = true;
			if (showSpinner) {
				if (this.manualSaving) return false;
				this.manualSaving = true;
			}

			this.success = null;

			try {
				const round = roundSnapshot || this.roundData || this.deepClone(this.activeRound);
				if (!round?.id) throw new Error('bulkSave: missing round id');

				const oldChecklistsSnapshot = this.deepClone(this.roundData?.oldChecklists || []);
				this.roundData = this.deepClone(round);
				this.roundData.oldChecklists = oldChecklistsSnapshot;

				console.log('bulkSave running (not early-returning)');

				this.questionParams.pageSize = 0;
				this.infoParams.pageSize = 0;
				this.questionParams.roundHistoryId = round.id;
				this.infoParams.roundHistoryId = round.id;

			
				const oldById = new Map();
					(this.roundData.oldChecklists || []).forEach(cl => {
					(cl.roundHistoryQuestions || []).forEach(q => oldById.set(q.id, q));
					});
				if (Array.isArray(round.checklists)) {
					for (const checklist of round.checklists) {
						for (const question of (checklist.roundHistoryQuestions || [])) {
							const oldQ = oldById.get(question.id);
							const oldAnswer = oldQ ? oldQ.answer : null; // ✅ treat missing as null

							if (question.answer === null) continue;

							if (question.answer !== oldAnswer) {
								question.roundHistoryQuestionListId = checklist.id;
								await this.updateRoundHistoryQuestion(question);
							}
						}
					}
					// refresh snapshot so later edits diff correctly
					await this.getRoundHistoryQuestionLists();
					this.roundData.oldChecklists = this.deepClone(this.roundHistoryQuestionLists);
					this.setRound(this.roundData);
				}

			
				for (let infoIndex = 0; infoIndex < (round.infos || []).length; infoIndex++) {
				const info = round.infos[infoIndex];
				const oldInfo = round.oldInfos?.[infoIndex];

				for (let itemIndex = 0; itemIndex < (info.roundHistoryItems || []).length; itemIndex++) {
					const item = info.roundHistoryItems[itemIndex];
					const oldItem = oldInfo?.roundHistoryItems?.[itemIndex];

					item.roundHistoryInfoId = info.id;

					if (!item?.id) continue;

					let compare = item.controlled;
					if (compare && typeof compare === 'object' && compare.toISOString) {
					compare = compare.toISOString();
					}
					if (typeof compare === 'string' && compare.includes('Z')) {
					compare = compare.slice(0, -1);
					}

					const oldControlled = oldItem?.controlled;

					if (compare !== oldControlled) {
					 await this.updateRoundHistoryItem(item);
					}

					const itemFlaws = item.flaws || [];
					for (let flawIndex = 0; flawIndex < itemFlaws.length; flawIndex++) {
					const flaw = itemFlaws[flawIndex];

					if (flaw.id) {
						const old = this.findOldFlawById(oldItem, flaw.id);

						if (old && flaw.done !== old.done) {
						await this.updateFlaw(flaw);
						}
					} else {
						flaw.roundHistoryItemId = item.id;
						flaw.roundHistoryInfoId = info.id;

						const dto = this.normalizeFlawForCreate(flaw);
						const data = await this.createFlaw(dto);
						flaw.id = data.id;
						if (flaw.clientKey) this.patchFlawIdByClientKey(flaw.clientKey, data.id);
						await this.tryUploadFlawImage(flaw);
					}
					}
				}

					const looseFlaws = (info.flaws || []).filter(f => !f?.roundHistoryItemId);
					for (let flawIndex = 0; flawIndex < looseFlaws.length; flawIndex++) {
						const flaw = looseFlaws[flawIndex];

						if (flaw.id) {
							const old = this.findOldFlawById(oldInfo, flaw.id);

							if (old && (flaw.done !== old.done || flaw.flawScale !== old.flawScale)) {
							await this.updateFlaw(flaw);
							}
						} else {
							flaw.roundHistoryInfoId = info.id;
							delete flaw.roundHistoryItemId;

							const dto = this.normalizeFlawForCreate(flaw);
							const data = await this.createFlaw(dto);
							flaw.id = data.id;
							if (flaw.clientKey) this.patchFlawIdByClientKey(flaw.clientKey, data.id);
							await this.tryUploadFlawImage(flaw);
						}
					}
				}

			
				await this.getRoundHistoryInfos();

				const infoList = [];
				this.roundHistoryInfos.forEach((info) => {
				info.roundHistoryItems?.sort((a, b) =>
					a.positionNumber > b.positionNumber ? 1 : a.positionNumber === b.positionNumber ? 0 : -1
				);
				infoList.push(info);
				});

				this.roundData.oldInfos = this.deepClone(infoList);

						await this.updateRoundHistory(this.roundData);
				this.setRound(this.roundData);
				this.saveStateSilent(this.roundData);

				this.success = true;
				await this.flushPendingMapItemExtras();
				await removeRoundDraft(this.roundData.id).catch(() => {});
				this.hasDraftInIdb = false;
				return true;
			} catch (e) {
				console.error('bulkSave failed', e);
				this.success = false;
				return false;
			} finally {
				if (showSpinner) this.manualSaving = false;
				this.syncInFlight = false;
			}
		},

		findFlawInStateById(flawId) {
			const base = this.roundData || this.activeRound;
			if (!base?.infos || !flawId) return null;

				for (const info of base.infos) {
					for (const item of (info.roundHistoryItems || [])) {
						const found = (item.flaws || []).find(f => f?.id === flawId);
						if (found) return found;
					}
				const foundLoose = (info.flaws || []).find(f => f?.id === flawId);
				if (foundLoose) return foundLoose;
			}
			return null;
		},

		setFlawOfflineFlagsInState({ flawId, fileLocalKey, pending }) {
			const target = this.findFlawInStateById(flawId);
			if (!target) return;

			this.$set(target, 'fileLocalKey', fileLocalKey);
			this.$set(target, 'filePendingUpload', pending);

			this.saveState(this.roundData || this.deepClone(this.activeRound));
		},

		async waitForSavesToFinish() {
			while (this.autosaving || this.manualSaving || this.syncInFlight) {
				await new Promise(r => setTimeout(r, 50));
			}
		},

	async setAllOk() {
		const snapshot = this.deepClone(this.activeRound);

		const now = this.setDate ? this.setDate() : new Date();
		const email = this.getUser?.email || snapshot.userName;

		for (const info of (snapshot.infos || [])) {
			for (const item of (info.roundHistoryItems || [])) {
			if (!item?.id) continue;

				const flaws = Array.isArray(item.flaws) ? item.flaws : [];
				const hasOpenFlaw = flaws.some(f => !f?.done);
				if (hasOpenFlaw) continue;

				if (!item.controlled && !item.continueWithoutAdding) {
					item.controlled = now;
					item.userName = email;
				}
			}
		}

		this.roundData = snapshot;
		this.saveState(snapshot); 

		if (!navigator.onLine) {
			this.saveMode = 'local';
			await this.persistDraftLocally();
			this.$toast?.info?.('Marked remaining items as OK (saved locally). Will sync when online.');
			return;
		}

		await this.forceSyncNow();
	},

		async pauseRound() {

			this.debouncedAutosave?.cancel?.();
  			await this.waitForSavesToFinish();
			const base = this.roundData || this.activeRound;
			console.log('pauseRound clicked', { isOnline: this.isOnline, navigatorOnLine: navigator.onLine });


			const snapshot = this.deepClone(base);

			const online = navigator.onLine;
			if (!online) {
				this.saveMode = 'local';

				this.roundData = snapshot;
				this.saveState(snapshot);

				await this.persistDraftLocally();

				try {
				const allFlaws = [];
				base?.infos?.forEach((info) => {
					(info.roundHistoryItems || []).forEach((item) => {
					(item.flaws || []).forEach((f) => allFlaws.push(f));
					});
					(info.flaws || []).forEach((f) => allFlaws.push(f));
				});

				for (const flaw of allFlaws) {
					await this.stashFlawImageIfNeeded(flaw);
				}

				console.log(
				'pending flags',
				(this.roundData?.infos || []).flatMap(i => [
					...(i.flaws || []),
					...((i.roundHistoryItems || []).flatMap(it => it.flaws || []))
				]).filter(f => f?.fileLocalKey || f?.filePendingUpload).map(f => ({ id: f.id, key: f.fileLocalKey }))
				);
				console.log('pendingUploadCount computed =', this.pendingUploadCount);


				this.roundData = this.deepClone(this.activeRound);
				} catch (e) {
				console.warn('pauseRound: stashFlawImageIfNeeded failed', e);
				}

				this.toDashboard();
				return;
			}

			const ok = await this.bulkSave(snapshot, { showSpinner: true });

			if (ok) {
				await this.clearLocalDraft();
				this.dirty = false;
				this.saveMode = 'server';
				this.toDashboard();
			} else {
				this.saveMode = 'local';
				await this.persistDraftLocally();
				this.toDashboard();
			}
		},

		async finishRound() {

			if (!this.isOnline) {
				this.$toast?.warning?.(this.$t('round_view.finish_requires_online_toast'));
				return;
			}
			this.debouncedAutosave?.cancel?.();
			await this.waitForSavesToFinish();

			const snapshot = this.deepClone(this.activeRound);
			snapshot.done = this.setDate();

			this.roundData = snapshot;
			this.saveState(snapshot);

			const roundId = snapshot?.id || this.activeRound?.id;

			try {
				await this.persistDraftLocally(); // should warn/force sync/throw if too large
			} catch (e) {
				// If draft can't be safely persisted and we can't sync, block finishing
				if (!navigator.onLine) {
				this.$toast?.error?.(
					'Offline storage is full. Please reconnect and sync before finishing the round.'
				);
				return;
				}

				// If online, try an immediate sync instead of blocking
				this.$toast?.warning?.('Storage is full. Trying to sync now…');
				// If this throws or fails, we’ll fall through to local fallback below
			}

			if (!navigator.onLine) {
				this.isDone = true;
				this.saveMode = 'local';
				this.toDashboard();
				return;
			}

			const ok = await this.bulkSave(snapshot, { showSpinner: true });
			this.isDone = true;

			if (!ok) {
				this.saveMode = 'local';
				try { await this.persistDraftLocally(); } catch (_) {}
				this.$toast?.warning?.('Could not finish syncing. The round is saved locally and will sync when possible.');
				this.toDashboard();
				return;
			}

			try {
				await this.flushPendingMapItemExtras();
				await this.refreshPendingMapItemExtraCount();

				await this.flushPendingUploads();

				await this.clearLocalDraft();

				if (roundId) {
					await removeOfflineFilesForRound(roundId);
				}

				this.dirty = false;
				this.saveMode = 'server';
			} finally {
				this.toDashboard();
			}
		},

		toDashboard() {
			this.$router.push({
				name: 'Dashboard'
			});
		},
		setDate() {
			let date = new Date();
			date.setHours(date.getHours() - date.getTimezoneOffset() / 60);

			return date;
		},
		setRoundDone() {
			this.isDone = true;
		},
		beforeWindowUnload(e) {
			if (this.isDone || !this.hasPendingUnsynced) return;
			e.preventDefault();
			e.returnValue = ''; 
		},
		confirmLeave() {
			const key = this.hasPendingUnsynced
				? 'round_view.confirm_leave_round_unsynced'
				: 'round_view.confirm_leave_round';

			return window.confirm(this.$t(key));
		},
		confirmLeaveRound() {
			return !this.isDone && !this.confirmLeave();
		},
		async dataUrlToBlob(dataUrl) {
			const res = await fetch(dataUrl);
			return await res.blob();
		},

		async compressImageFileIfNeeded(
			file,
			{
				maxBytes = 1_500_000,      // target cap
				maxDim = 1600,             // max width/height
				preferMime = 'image/jpeg', // or 'image/webp'
				startQuality = 0.85,
				minQuality = 0.55,
			} = {}
			) {
			if (!file) return file;

			const isImage = file.type?.startsWith('image/');
			if (!isImage) return file;

			const img = await new Promise((resolve, reject) => {
				const url = URL.createObjectURL(file);
				const i = new Image();
				i.onload = () => { URL.revokeObjectURL(url); resolve(i); };
				i.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image decode failed')); };
				i.src = url;
			});

			let { width, height } = img;

			const needsResize = Math.max(width, height) > maxDim;
			const needsBytes = file.size > maxBytes;

			if (!needsResize && !needsBytes) return file;

			const scale = Math.min(1, maxDim / Math.max(width, height));
			width = Math.max(1, Math.round(width * scale));
			height = Math.max(1, Math.round(height * scale));

			const canvas = document.createElement('canvas');
			canvas.width = width;
			canvas.height = height;

			const ctx = canvas.getContext('2d');
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = 'high';

			ctx.drawImage(img, 0, 0, width, height);

			let quality = startQuality;

			const toBlob = (q) =>
			new Promise((resolve, reject) => {
				canvas.toBlob((b) => {
				if (!b) reject(new Error('canvas.toBlob returned null'));
				else resolve(b);
				}, preferMime, q);
			});


			let blob = await toBlob(quality);
			if (!blob) return file; // fallback

			while (blob.size > maxBytes && quality > minQuality) {
				quality = Math.max(minQuality, quality - 0.08);
				blob = await toBlob(quality);
				if (!blob) break;
			}

			while (blob && blob.size > maxBytes && Math.max(width, height) > 900) {
				width = Math.max(1, Math.round(width * 0.85));
				height = Math.max(1, Math.round(height * 0.85));
				canvas.width = width;
				canvas.height = height;
				ctx.drawImage(img, 0, 0, width, height);

				quality = Math.max(minQuality, quality - 0.05);
				blob = await toBlob(quality);
			}

			if (!blob) return file;

			const ext = preferMime.includes('webp') ? 'webp' : 'jpg';
			const baseName = (file.name || 'flaw').replace(/\.[^.]+$/, '');
			const outName = `${baseName}.${ext}`;

			return new File([blob], outName, { type: preferMime });
		},



		async stashFlawImageIfNeeded(flaw) {
			if (!flaw) return;

			if (flaw.fileLocalKey) return;

			const isDataUrl = typeof flaw.file === 'string' && flaw.file.startsWith('data:');
			const isBlob = flaw.file instanceof Blob; // File is also a Blob

			if (!isDataUrl && !isBlob) {
				return;
			}

			let rawFile;
			if (isDataUrl) {
				const blobRaw = await this.dataUrlToBlob(flaw.file);
				rawFile = new File([blobRaw], 'flaw.jpg', { type: blobRaw.type || 'image/jpeg' });
			} else {
				const name = flaw.file.name || 'flaw.jpg';
				const type = flaw.file.type || 'image/jpeg';
				rawFile = new File([flaw.file], name, { type });
			}

			const compressedFile = await this.compressImageFileIfNeeded(rawFile, {
				maxBytes: 1_500_000,
				maxDim: 1600,
				preferMime: 'image/jpeg',
			});

			const key = await putOfflineFile({
				blob: compressedFile,
				mime: compressedFile.type,
				filename: compressedFile.name || 'flaw.jpg',
				meta: { roundId: this.activeRound?.id, flawId: flaw.id || null }
			});

			if (flaw?.id) {
				this.setFlawOfflineFlagsInState({
				flawId: flaw.id,
				fileLocalKey: key,
				pending: true
				});
			}

			flaw.file = null;
			flaw.fileLocalKey = key;
			flaw.filePendingUpload = true;

			this.saveMode = 'local';

			this.$forceUpdate();
		},


		async tryUploadFlawImage(flaw) {
			if (!flaw?.id) return;

			if (!navigator.onLine) {
				await this.stashFlawImageIfNeeded(flaw);
				return;
			}

			try {
				if (flaw.fileLocalKey) {
					const stored = await getOfflineFile(flaw.fileLocalKey);
					if (!stored?.blob) return;

					const name = stored.filename || 'flaw.jpg';
					const type = stored.mime || stored.blob.type || 'image/jpeg';

					const fileObjRaw = new File([stored.blob], name, { type });

					const fileObj = await this.compressImageFileIfNeeded(fileObjRaw, {
						maxBytes: 1_500_000,
						maxDim: 1600,
						preferMime: 'image/jpeg',
					});

					await this.saveFile({ file: fileObj, flawId: flaw.id });

					await removeOfflineFile(flaw.fileLocalKey);
					this.setFlawOfflineFlagsInState({
						flawId: flaw.id,
						fileLocalKey: null,
						pending: false
					});
					flaw.fileLocalKey = null;
					flaw.filePendingUpload = false;

					this.saveState();
					return;
				}

				if (flaw.file instanceof Blob) {
					const name = flaw.file.name || 'flaw.jpg';
					const type = flaw.file.type || 'image/jpeg';
					const raw =
						flaw.file instanceof File
						? flaw.file
						: new File([flaw.file], name, { type });

					const fileObj = await this.compressImageFileIfNeeded(raw, {
						maxBytes: 1_500_000,
						maxDim: 1600,
						preferMime: 'image/jpeg',
					});

					await this.saveFile({ file: fileObj, flawId: flaw.id });

					flaw.file = null;
					flaw.filePendingUpload = false;
					this.saveState();
					return;
				}


				if (flaw.file && typeof flaw.file === 'string' && flaw.file.startsWith('data:')) {
				const blob = await this.dataUrlToBlob(flaw.file);

				const raw = new File([blob], 'flaw.jpg', { type: blob.type || 'image/jpeg' });

				const fileObj = await this.compressImageFileIfNeeded(raw, {
				maxBytes: 1_500_000,
				maxDim: 1600,
				preferMime: 'image/jpeg',
				});

				await this.saveFile({ file: fileObj, flawId: flaw.id });

				flaw.filePendingUpload = false;
				this.saveState();
				}
			}  catch (e) {
				console.warn('tryUploadFlawImage failed, stashing locally', e);

				await this.stashFlawImageIfNeeded(flaw);

				if (navigator.onLine) {
					this.$toast?.error?.(
					'Photo upload failed. It has been saved on this device and will retry automatically. ' +
					'If this keeps happening, check your connection and try “Sync now”.'
					);
				} else {
					this.$toast?.warning?.('You are offline. Photo saved on this device and will upload when online.');
				}
			}
		},

		async flushPendingUploads() {
			if (!navigator.onLine) return;

			this.roundData = this.deepClone(this.activeRound);
			this.saveStateSilent(this.roundData);


			const allFlaws = [];

			this.roundData.infos?.forEach((info) => {
				info.roundHistoryItems?.forEach((item) => {
				(item.flaws || []).forEach((f) => allFlaws.push(f));
				});
				(info.flaws || []).forEach((f) => allFlaws.push(f));
			});

			for (const flaw of allFlaws) {
				if (flaw?.fileLocalKey || flaw?.filePendingUpload) {
					await this.tryUploadFlawImage(flaw);
				}
			}

			this.saveStateSilent();
		},

		async flushPendingMapItemExtras() {
			if (!navigator.onLine) return;

			const roundId = this.activeRound?.id;
			if (!roundId) return;

			const pending = await listPendingMapItemExtras(roundId);
			for (const job of pending) {
				try {
					await MapItemService.upsertMapItemExtra(job.mapItemId, job.extra);
					await removePendingMapItemExtra(roundId, job.mapItemId);
					await this.refreshPendingMapItemExtraCount();
				} catch (e) {
					// stop early; keep remaining jobs for later
					console.warn('flushPendingMapItemExtras failed for', job.mapItemId, e);
					return;
				}
			}
		},

		onOffline() {
			this.isOnline = false;
			if (this.hasPendingUnsynced) {
				this.saveMode = 'local';
			}
		},

		async onOnline() {
			this.isOnline = true;

			const roundId = this.activeRound?.id;
			if (roundId) {
				const draft = await getRoundDraft(roundId);
				if (draft?.round) {
				this.saveMode = 'local';
				this.dirty = true;                 // force autosave to actually sync
				this.SET_ACTIVEROUND(draft.round); // restore local unsynced state into memory
				}
			}

			await this.autosave();

			await this.flushPendingMapItemExtras();
			await this.refreshPendingMapItemExtraCount();
			await this.flushPendingUploads();

			await this.refreshHasDraft();
			const nothingPending =
				!this.dirty &&
				this.pendingUploadCount === 0 &&
				this.pendingMapItemExtraCount === 0 &&
				this.saveMode !== 'local' &&
				!this.hasDraftInIdb;

			if (nothingPending && roundId) {
				await this.cleanupOfflineForRound(roundId);
			}
		},
		async forceSyncNow() {
			 if (!navigator.onLine) return;

			this.debouncedAutosave?.cancel?.();

			const snapshot = this.deepClone(this.activeRound);
			await this.persistDraftLocally();

			const ok = await this.bulkSave(snapshot, { showSpinner: true });
			if (ok) {
				await this.flushPendingMapItemExtras();
				await this.refreshPendingMapItemExtraCount();
				await this.flushPendingUploads();
				await this.cleanupOfflineForRound(this.activeRound.id);
				this.dirty = false;
				this.saveMode = 'server';
			} else {
				this.saveMode = 'local';
				await this.persistDraftLocally();
			}
		}

	},
	async created() {
		window.addEventListener('online', this.onOnline);
		window.addEventListener('offline', this.onOffline);
		await this.refreshPendingMapItemExtraCount();
		await this.preloadFlawTaxonomy();


        this.debouncedAutosave = debounce(() => {
			this.autosave();
		}, 500);

		const isOnIOS =
      	navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPhone/i);
		const eventName = 'beforeunload';
		window.addEventListener(eventName, this.beforeWindowUnload);

		this.tabs = [
			{ name: this.$t('round_view.tab_drawing'), icon: 'fa-map', disabled: true },
			{ name: this.$t('round_view.tab_list'), icon: 'fa-list', disabled: false },
			{ name: this.$t('round_view.tab_questions'), icon: 'fa-clipboard-list', disabled: false },
			{ name: this.$t('round_view.tab_summary'), icon: 'fa-chart-bar', disabled: false }
		];


		if (!this.isMobile) this.SET_COLLAPSED(true);

		if (this.activeRound) {
			this.roundData = this.deepClone(this.activeRound);
			await this.getRoundHistoryQuestionLists();
			this.roundData.oldChecklists = this.deepClone(this.roundHistoryQuestionLists);
			this.saveStateSilent(this.roundData);

			this.setRound(this.roundData);

			if (this.roundData.currentInfo || this.roundData.currentInfo === 0) {
				this.loaded = true;
			}

			if (!this.roundData.checklists || !this.roundData.checklists.length) {
				this.tabs[2].disabled = true;
			}

			this.roundData.currentTab =
       		 this.roundData.currentTab || this.roundData.currentTab === 0
        	? this.roundData.currentTab
        	: 1;
			this.selectedTab = this.roundData.currentTab;

			if (!this.roundData.currentInfo && this.roundData.currentInfo !== 0) {
				this.roundData.currentInfo = 0;
			}

			this.ResetRoundImages();

			await this.asyncForEach(this.roundData.infos, async (info) => {
			if (!info?.fileId) return;

			const hydrated = await this.$store.dispatch('fileStore/hydrateRoundImageFromOffline', {
				fileId: info.fileId,
				mapFileLocalKey: info.mapFileLocalKey // <-- persisted key on info
			});

			if (hydrated) {
				this.tabs[0].disabled = false;
				return;
			}

			if (!navigator.onLine) return;

			await this.getFile(info.fileId);

			const blob = await this.ensureBlob(this.file);
			if (!blob) return;

			const objectUrl = URL.createObjectURL(blob);
			this.AddRoundImage({ fileId: info.fileId, img: objectUrl, isObjectUrl: true });

			const key = await putOfflineFile({
				blob,
				mime: blob.type || 'application/octet-stream',
				filename: `${info.fileId}`,
				meta: { roundId: this.activeRound?.id, fileId: info.fileId }
			});

			this.$set(info, 'mapFileLocalKey', key);

			this.tabs[0].disabled = false;
			});

			this.saveStateSilent();
			this.loaded = true;
			await this.refreshHasDraft();
		}

	    if (navigator.onLine) {
			if (!this.dirty && this.saveMode !== 'local') {
				await this.clearLocalDraft();
			}
			await this.flushPendingUploads();
		}
		this.autosaveReady = true;
	},

	beforeDestroy() {
		this.isLeavingView = true;
		const roundId = this.activeRound?.id;

		window.removeEventListener('online', this.onOnline);
		window.removeEventListener('offline', this.onOffline);
		window.removeEventListener('beforeunload', this.beforeWindowUnload);

		if (roundId && (this.isDone || !this.hasPendingUnsynced)) {
			this.cleanupOfflineForRound(roundId).catch(() => {});
		}

		this.SET_COLLAPSED(false);
		this.ResetRoundImages();
	},

	beforeRouteLeave(to, from, next) {
		const roundId = this.activeRound?.id;

		if (this.confirmLeaveRound()) {
			next(false);
			return;
		}

		this.isLeavingView = true;
		this.debouncedAutosave?.cancel?.();

		if (!roundId) return next();

		if (this.isDone || !this.hasPendingUnsynced) {
			this.wipeRoundLocalData(roundId)
			.catch(err => console.warn('wipeRoundLocalData failed', err))
			.finally(() => next());
		} else {
			next();
		}
	},
};
</script>

<style lang="scss" scoped>
@import "@/assets/colors.scss";

.top-box {
  background: $lightest-grey;
  padding: 10px 5px 10px 5px;
  display: block;
  border-radius: 2px;
  margin-bottom: 10px;
  .row {
    display: flex;
  }
  .field {
    &.name {
      flex: 1;
    }
    &.description {
      flex: 2;
    }
    margin-left: 10px;
    margin-right: 10px;
    .label {
      font-size: 12px;
    }
  }
  .textarea,
  .input {
    background: white;
    border-radius: 2px;
  }
  .buttons {
    margin: 0;
    margin-top: 19px;
    margin-left: 10px;
    .button {
      border-radius: 2px;
      width: 110px;
    }
  }
}
.tabs.is-boxed {
  margin-bottom: 0;
}
</style>
